<template>
  <div class="page-shell">
    <div v-if="errorMessage" class="error-banner">
      {{ errorMessage }}
    </div>

    <div v-if="isSubmitted" class="success-row">
      <AppCard class="success-card">
        <v-icon size="56" color="success">mdi-check-circle-outline</v-icon>
        <h2 class="success-title">Application Submitted</h2>
        <p class="success-subtitle">
          Your application has been submitted successfully{{
            companyDisplayName ? ` to ${companyDisplayName}` : ""
          }}. It is now under review. We will notify you once the review is complete.
        </p>
        <v-btn
          color="primary"
          variant="flat"
          @click="router.push({ name: 'vendor.dashboard' })"
        >
          Back to Dashboard
        </v-btn>
      </AppCard>
    </div>
    <div v-else>
      <div v-if="isLoading" class="loading-row">
        <v-progress-circular indeterminate size="18" width="2" />
        <span>Loading association...</span>
      </div>
      <template v-else>
        <template v-if="company">
          <div class="application-header">
            <div class="application-header__icon">
              <v-icon size="22">mdi-card-account-details-star-outline</v-icon>
            </div>
            <div class="application-header__copy">
              <h3>Membership Application</h3>
              <p>
                {{
                  hasCompanyPlans
                    ? "Review your approved business details and choose a membership plan."
                    : "Review your approved business details and submit your application."
                }}
              </p>
            </div>
          </div>
          <div class="review-list">
            <div class="review-section">
              <div class="review-section-title">Company Information</div>
              <div class="review-card">
                <div class="review-row review-row--title-bottom">
                  <span>Name</span><strong>{{ company.name || "-" }}</strong>
                </div>
                <div class="review-row review-row--title-bottom">
                  <span>Address</span><strong>test</strong>
                </div>
              </div>
            </div>

            <div class="review-section">
              <div class="review-section-title">Business Information</div>
              <div class="review-card">
                <div class="review-row review-row--title-bottom">
                  <span>Name</span
                  ><strong>{{ approval.businessInfo.business_name || "-" }}</strong>
                </div>
                <div class="review-row review-row--title-bottom">
                  <span>Type</span
                  ><strong>{{ approval.businessInfo.business_type || "-" }}</strong>
                </div>
                <div class="review-row review-row--title-bottom">
                  <span>Email</span
                  ><strong>{{ approval.businessInfo.business_email || "-" }}</strong>
                </div>
                <div class="review-row review-row--title-bottom">
                  <span>Phone</span
                  ><strong>{{ approval.businessInfo.business_phone || "-" }}</strong>
                </div>
              </div>
            </div>
            <div class="review-section">
              <div class="review-section-title">Business Address</div>
              <div class="review-card">
                <div class="review-row review-row--title-bottom">
                  <span>Address</span
                  ><strong>{{ approval.addressInfo.add1 || "-" }}</strong>
                </div>
                <div class="review-row review-row--title-bottom">
                  <span>City</span><strong>{{ approval.addressInfo.city || "-" }}</strong>
                </div>
                <div class="review-row review-row--title-bottom">
                  <span>State</span
                  ><strong>{{ approval.addressInfo.state || "-" }}</strong>
                </div>
                <div class="review-row review-row--title-bottom">
                  <span>Zip</span><strong>{{ approval.addressInfo.zip || "-" }}</strong>
                </div>
              </div>
            </div>
            <div class="review-section">
              <div class="review-section-title">Documents</div>
              <div class="review-card">
                <div
                  class="review-row review-row--title-bottom"
                  v-for="(doc, index) in approval.documents"
                  :key="index"
                >
                  <strong class="text-capitalize">{{
                    doc.type.split("_").join(" ") || "Document"
                  }}</strong>
                  <div class="review-file">
                    <img
                      v-if="doc.url"
                      class="review-file-thumb"
                      :src="doc.url"
                      :alt="doc.type || 'ID Proof (Front)'"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div v-if="hasCompanyPlans" class="review-section">
              <div class="review-section-title">Choose Membership Plans</div>
              <div class="review-card">
                <MembershipPlans
                  :company_id="getRouteCompanyId()"
                  :package_id="selectedPlanId ? String(selectedPlanId) : ''"
                  :billing_interval="selectedBillingInterval"
                  :errors="{}"
                  @update:package_id="handleMembershipPackageUpdate"
                  @update:billing_interval="handleMembershipBillingUpdate"
                />
              </div>
            </div>
          </div>
          <div v-if="!isSubmitted" class="card">
            <div
              v-if="hasCompanyPlans && selectedPrice && Number(selectedPrice.amount) > 0"
              class="section"
            >
              <div class="section-title">Payment</div>
              <PaymentBlock
                ref="paymentBlockRef"
                :publishableKey="stripeConfig.publishableKey || companyPublishableKey"
                :stripeAccount="stripeConfig.stripeAccount"
                :amount="selectedPrice.amount"
              />
            </div>

            <div class="actions">
              <AppButtonFlat
                block
                append-icon="mdi-arrow-right"
                :loading="isSubmitting"
                :disabled="
                  isSubmitting ||
                  (hasCompanyPlans && (!selectedPlanId || !selectedPriceId))
                "
                @click="handleApply"
              >
                {{ hasCompanyPlans ? "Apply Now" : "Submit Application" }}
              </AppButtonFlat>
            </div>
          </div>
        </template>

        <AppCard v-else class="empty-card">Association not found.</AppCard>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import AppCard from "@/shared/components/ui/AppCard.vue";
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";
import { fetchCompanyDetails } from "@/modules/vendor/services/vendors.api";
import PaymentBlock from "@/shared/components/ui/flow/PaymentBlock.vue";
import MembershipPlans from "@/modules/vendor/components/MembershipPlans.vue";
import { useAppStore } from "@/shared/stores/app";
import {
  createVendorMembershipIntent,
  finalizeVendorMembership,
  sendVendorRequest,
  sendVendorRequestWithoutPrice,
} from "@/shared/services/vendor-requests.api";

type CompanyAddress = {
  city?: string | null;
  state?: string | null;
  country?: string | null;
  postal_code?: string | null;
};

type CompanyPlanPrice = {
  id: number;
  amount: number;
  billing_cycle?: string | null;
  interval_label?: string | null;
};

type CompanyPlan = {
  id: number;
  name: string;
  highlight: string | null;
  description: string[];
  prices: CompanyPlanPrice[];
};

type ApprovalContactInfo = {
  email: string;
  fname: string;
  lname: string;
  phone: string;
  email_alt: string | null;
  phone_alt: string | null;
};

type ApprovalAddressInfo = {
  lat: string | null;
  lng: string | null;
  zip: string;
  add1: string;
  add2: string;
  city: string;
  state: string;
  county: string | null;
  country: string | null;
  is_default: number;
  country_code: string | null;
};

type ApprovalDocument = {
  name: string;
  path: string;
  size: number;
  type: string;
  url: string | null;
  mime_type: string;
};

type ApprovalBusinessInfo = {
  business_name: string;
  business_type: string;
  business_email: string;
  business_phone: string;
};

type ApprovalInfo = {
  id: number;
  contact_info: ApprovalContactInfo;
  address_info: ApprovalAddressInfo;
  documents: ApprovalDocument[];
  business_info: ApprovalBusinessInfo;
};

type CompanyDetail = {
  name: string;
  code: string;
  phone: string | null;
  email: string | null;
  address: CompanyAddress;
  company_plans: CompanyPlan[];
  company?: {
    id: number;
    publishable_key: string;
  };
  application_status: string;
  application_id: number | null;
  approved: boolean;
  approval_info: ApprovalInfo | null;
};

const route = useRoute();
const router = useRouter();
const appStore = useAppStore();

const isLoading = ref(false);
const errorMessage = ref("");
const company = ref<CompanyDetail | null>(null);
const companyDisplayName = computed(() => String(company.value?.name || "").trim());
const hasCompanyPlans = computed(() => (company.value?.company_plans || []).length > 0);
const selectedPlanId = ref<number | null>(null);
const selectedPriceId = ref<number | null>(null);
const selectedBillingInterval = ref<"monthly" | "yearly">("monthly");
const selectedPlan = ref<CompanyPlan | null>(null);
const isSubmitting = ref(false);
const isSubmitted = ref(false);
const stripeConfig = reactive<{ publishableKey: string; stripeAccount: string }>({
  publishableKey: "",
  stripeAccount: "",
});
const paymentBlockRef = ref<{
  validate: () => Promise<void>;
  confirmPayment: (
    clientSecret: string,
    billingDetails: Record<string, unknown>
  ) => Promise<{ paymentIntentId?: string; paymentMethodId?: string }>;
  confirmSetup: (
    clientSecret: string,
    billingDetails: Record<string, unknown>
  ) => Promise<{ setupIntentId?: string; paymentMethodId?: string }>;
} | null>(null);

function getRouteCompanyId(): number | null {
  const raw = route.params.id;
  const value = Array.isArray(raw) ? raw[0] : raw;
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : null;
}

const addressLine1 = computed(() => {
  const city = String(company.value?.address?.city || "").trim();
  const state = String(company.value?.address?.state || "").trim();
  if (city && state) return `${city}, ${state}`;
  return city || state || "—";
});

const addressLine2 = computed(() => {
  const country = String(company.value?.address?.country || "").trim();
  const postal = String(company.value?.address?.postal_code || "").trim();
  return [country, postal].filter(Boolean).join(" • ");
});

const companyPublishableKey = computed(
  () => company.value?.company?.publishable_key || ""
);

const approval = computed(() => {
  const info = company.value?.approval_info || null;
  const id =
    info && typeof info === "object" ? Number((info as ApprovalInfo).id) || null : null;
  const contactInfo =
    info && typeof info === "object" ? (info as ApprovalInfo).contact_info : null;
  const addressInfo =
    info && typeof info === "object" ? (info as ApprovalInfo).address_info : null;
  const businessInfo =
    info && typeof info === "object" ? (info as ApprovalInfo).business_info : null;
  const documentsRaw =
    info && typeof info === "object" ? (info as ApprovalInfo).documents : [];
  const documents = Array.isArray(documentsRaw) ? documentsRaw : [];
  const contactName = (() => {
    const name = `${String(contactInfo?.fname || "").trim()} ${String(
      contactInfo?.lname || ""
    ).trim()}`.trim();
    return name || "-";
  })();

  return {
    info,
    id,
    contactInfo,
    addressInfo,
    businessInfo,
    documents,
    contactName,
  };
});

function formatBytes(value: unknown) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let size = n;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  const digits = unitIndex === 0 ? 0 : size >= 10 ? 1 : 2;
  return `${size.toFixed(digits)} ${units[unitIndex]}`;
}

// const selectedPlan = computed(() => {
//   const id = selectedPlanId.value;
//   if (!id) return null;
//   return (company.value?.company_plans || []).find((p) => p.id === id) || null;
// });

const selectedPrice = computed(() => {
  const plan = selectedPlan.value;
  const id = selectedPriceId.value;
  if (!plan || !id) return null;
  return (plan.prices || []).find((p) => p.id === id) || null;
});

function selectPlan(id: number) {
  selectedPlanId.value = id;
  const plan = (company.value?.company_plans || []).find((p) => p.id === id);
  selectedPlan.value = plan || null;
}

function resolvePriceIdForInterval(
  plan: CompanyPlan | null,
  interval: "monthly" | "yearly"
) {
  if (!plan?.prices?.length) return null;
  const matched = plan.prices.find((price: any) => {
    const billingCycle = String(price?.billing_cycle || "").toLowerCase();
    const intervalLabel = String(price?.interval_label || "").toLowerCase();
    return billingCycle.includes(interval) || intervalLabel.includes(interval);
  });
  return (
    matched?.id ??
    plan.prices[interval === "yearly" ? 1 : 0]?.id ??
    plan.prices[0]?.id ??
    null
  );
}

function handleMembershipPackageUpdate(value: string) {
  const id = Number(value);
  if (!Number.isFinite(id)) {
    selectedPlanId.value = null;
    selectedPriceId.value = null;
    selectedPlan.value = null;
    return;
  }

  selectPlan(id);
  selectedPriceId.value = resolvePriceIdForInterval(
    selectedPlan.value,
    selectedBillingInterval.value
  );
}

function handleMembershipBillingUpdate(value: "monthly" | "yearly") {
  selectedBillingInterval.value = value;
  selectedPriceId.value = resolvePriceIdForInterval(selectedPlan.value, value);
}

function getBillingIntervalFromSelection() {
  return selectedBillingInterval.value;
}

// New handler for free plans
async function handleApplyWithoutPrice() {
  try {
    isSubmitting.value = true;
    errorMessage.value = "";

    const companyId = getRouteCompanyId();
    if (!companyId) throw new Error("Invalid company id");

    const payload: Record<string, string | undefined> = {
      company_id: String(companyId),
      approved_application_id: approval.value.id ? String(approval.value.id) : undefined,
    };

    if (selectedPlanId.value) {
      payload.company_plan_id = String(selectedPlanId.value);
    }

    if (selectedPriceId.value) {
      payload.company_price_id = String(selectedPriceId.value);
    }

    const resp: any = await sendVendorRequestWithoutPrice(payload);
    // console.log({ resp });
    isSubmitted.value = true;
    // router.push({ name: "vendor.dashboard" });
  } catch (err) {
    console.error("Failed to apply without price", err);
    errorMessage.value =
      (err as Error)?.message || "Unable to apply for free plan. Please try again.";
  } finally {
    isSubmitting.value = false;
  }
}

async function loadCompany() {
  const id = getRouteCompanyId();
  if (!id) {
    company.value = null;
    errorMessage.value = "Invalid association id.";
    return;
  }

  isLoading.value = true;
  errorMessage.value = "";
  try {
    const response: any = await fetchCompanyDetails(id);
    const root = response?.data ?? response;
    const payload = root?.data ?? root;
    company.value = payload as CompanyDetail;
    if (hasCompanyPlans.value) {
      const firstPlanId = company.value?.company_plans?.[0]?.id ?? null;
      selectedPlanId.value = firstPlanId;
      selectedPlan.value = company.value?.company_plans?.[0] ?? null;
      selectedPriceId.value = resolvePriceIdForInterval(
        selectedPlan.value,
        selectedBillingInterval.value
      );
    } else {
      selectedPlanId.value = null;
      selectedPlan.value = null;
      selectedPriceId.value = null;
    }

    // Prefer company-specific publishable key when available.
    if (company.value?.company?.publishable_key) {
      stripeConfig.publishableKey = company.value.company.publishable_key;
      console.log("Loaded company details", stripeConfig.publishableKey);
    }
  } catch (error) {
    console.error(error);
    errorMessage.value = "Unable to load association. Please try again.";
    company.value = null;
  } finally {
    isLoading.value = false;
  }
}

async function handleApply() {
  if (isSubmitting.value) return;
  const companyId = getRouteCompanyId();
  if (!companyId) return;

  if (!hasCompanyPlans.value) {
    await handleApplyWithoutPrice();
    return;
  }

  if (!selectedPlanId.value || !selectedPriceId.value) return;

  // If selected price is zero, delegate to the free-handler and skip payment flow
  if (selectedPrice.value && Number(selectedPrice.value.amount) === 0) {
    handleApplyWithoutPrice();
    return;
  }

  try {
    isSubmitting.value = true;
    errorMessage.value = "";

    // Same response shape as `createVendorRequest()` (vendor_id, company_vendor_id, company_id).
    const vendorResponse: any = await sendVendorRequest({
      company_id: companyId,
      approved_application_id: approval.value.id ? String(approval.value.id) : undefined,
      company_plan_id: String(selectedPlanId.value),
      company_price_id: String(selectedPriceId.value),
    });

    const vendorId = vendorResponse?.vendor_id || vendorResponse?.data?.vendor_id;
    const companyVendorId =
      vendorResponse?.company_vendor_id || vendorResponse?.data?.company_vendor_id;

    if (!vendorId) throw new Error("Failed to create vendor request.");

    const intentResponse: any = await createVendorMembershipIntent({
      company_id: companyId,
      company_vendor_id: companyVendorId,
      company_plan_id: String(selectedPlanId.value),
      company_price_id: String(selectedPriceId.value),
      billing_interval: getBillingIntervalFromSelection(),
    });

    const intentPayload =
      intentResponse?.original?.data || intentResponse?.data || intentResponse;

    const clientSecret =
      intentPayload?.clientSecret ||
      intentPayload?.client_secret ||
      intentResponse?.clientSecret ||
      intentResponse?.client_secret ||
      intentResponse?.data?.clientSecret ||
      intentResponse?.data?.client_secret;

    const intentType =
      intentPayload?.intentType ||
      intentPayload?.intent_type ||
      intentPayload?.intentType?.replace?.("-", "_") ||
      intentPayload?.intent_type?.replace?.("-", "_") ||
      intentResponse?.data?.intentType ||
      intentResponse?.data?.intent_type ||
      "payment";

    const membershipId =
      intentPayload?.membershipId ||
      intentPayload?.membership_id ||
      intentResponse?.data?.membershipId ||
      intentResponse?.data?.membership_id;

    const intentId =
      intentPayload?.intentId ||
      intentPayload?.intent_id ||
      intentPayload?.stripeIntentId ||
      intentPayload?.stripe_intent_id ||
      intentResponse?.data?.intentId ||
      intentResponse?.data?.intent_id ||
      intentResponse?.data?.stripeIntentId ||
      intentResponse?.data?.stripe_intent_id;

    const stripeIntentId =
      intentPayload?.stripeIntentId ||
      intentPayload?.stripe_intent_id ||
      intentResponse?.stripeIntentId ||
      intentResponse?.stripe_intent_id ||
      intentResponse?.data?.stripeIntentId ||
      intentResponse?.data?.stripe_intent_id ||
      intentId;

    stripeConfig.publishableKey =
      intentPayload?.publishableKey ||
      intentPayload?.publishable_key ||
      intentPayload?.stripePublishableKey ||
      intentPayload?.stripe_publishable_key ||
      companyPublishableKey.value ||
      stripeConfig.publishableKey ||
      "";
    stripeConfig.stripeAccount =
      intentPayload?.stripeAccount ||
      intentPayload?.stripe_account ||
      intentPayload?.stripe_account_id ||
      intentPayload?.stripeAccountId ||
      intentPayload?.stripe_accountID ||
      intentPayload?.account ||
      intentPayload?.account_id ||
      intentPayload?.connect_account ||
      intentPayload?.connect_account_id ||
      intentPayload?.stripe_connect_account ||
      intentPayload?.stripe_connect_account_id ||
      intentPayload?.connectedAccountId ||
      intentPayload?.connected_account_id ||
      stripeConfig.stripeAccount ||
      "";

    if (!clientSecret) throw new Error("Missing client secret from payment response.");
    if (!paymentBlockRef.value) throw new Error("Payment form is not ready.");

    const billingDetails = {
      name: approval.value.businessInfo?.business_name || company.value?.name || "",
      email:
        approval.value.businessInfo?.business_email ||
        approval.value.contactInfo?.email ||
        "",
      phone:
        approval.value.businessInfo?.business_phone ||
        approval.value.contactInfo?.phone ||
        "",
      address: {
        line1: approval.value.addressInfo?.add1 || "",
        line2: approval.value.addressInfo?.add2 || "",
        city: approval.value.addressInfo?.city || "",
        state: approval.value.addressInfo?.state || "",
        postal_code: approval.value.addressInfo?.zip || "",
      },
    };

    await paymentBlockRef.value.validate();

    const confirmation =
      intentType === "setup" || intentType === "setup_intent"
        ? await paymentBlockRef.value.confirmSetup(clientSecret, billingDetails)
        : await paymentBlockRef.value.confirmPayment(clientSecret, billingDetails);

    const paymentIntentId = (confirmation as any).paymentIntentId;
    const setupIntentId = (confirmation as any).setupIntentId;
    const paymentMethodId = (confirmation as any).paymentMethodId;

    await finalizeVendorMembership({
      company_id: companyId,
      membership_id: membershipId,
      vendor_id: vendorId,
      payment_intent_id: paymentIntentId,
      setup_intent_id: setupIntentId,
      payment_method_id: paymentMethodId,
      stripe_intent_id: paymentIntentId || setupIntentId || stripeIntentId,
      intent_type: intentType,
      intent_id: intentId,
    });

    isSubmitted.value = true;
  } catch (error) {
    console.error("Failed to submit vendor membership request", error);
    errorMessage.value =
      (error as Error)?.message ||
      "Unable to proceed with application. Please try again.";
  } finally {
    isSubmitting.value = false;
  }
}

onMounted(() => {
  void loadCompany();
});
</script>

<style scoped>
.page-shell {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 10px !important;
  background: #f6f7f9;
  min-height: calc(100vh - 140px);
}

.loading-row {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: var(--color-subtitle);
  font-size: 14px;
}

.error-banner {
  background: rgba(239, 68, 68, 0.08);
  color: #b91c1c;
  border: 1px solid rgba(239, 68, 68, 0.18);
  padding: 10px 12px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 14px;
}

.application-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 2px 4px;
}

.application-header__icon {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  flex: 0 0 auto;
  color: #e4572e;
  background: rgba(228, 87, 46, 0.12);
  border-radius: 10px;
}

.application-header__copy {
  min-width: 0;
}

.application-header__copy h3 {
  margin: 0;
  color: #0f172a;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.2;
}

.application-header__copy p {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.35;
}

.card {
  padding: 16px;
  border-radius: 18px;
}

.kicker {
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.12em;
  color: rgba(15, 23, 42, 0.55);
}

.title {
  margin-top: 8px;
  font-weight: 900;
  font-size: 20px;
  color: #0b1220;
  line-height: 1.15;
}

.subtitle {
  margin-top: 6px;
  font-size: 12px;
  font-weight: 700;
  color: rgba(15, 23, 42, 0.65);
}

.meta {
  margin-top: 10px;
  color: rgba(15, 23, 42, 0.75);
  font-weight: 700;
}

.meta-row {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.meta-sub {
  margin-left: 24px;
  margin-top: 2px;
  font-size: 12px;
  color: #e4572e;
  font-weight: 700;
}

.section {
  margin-top: 16px;
}

.section-title {
  font-size: 13px;
  font-weight: 900;
  color: #0b1220;
  margin-bottom: 8px;
}

.review-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}

.review-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  font-size: 13px;
  color: rgba(15, 23, 42, 0.72);
}

.review-row strong {
  color: #0b1220;
  font-weight: 900;
  text-align: right;
}

.muted {
  color: rgba(15, 23, 42, 0.55);
  font-size: 13px;
  font-weight: 700;
}

.plan-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.plan-card {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 14px;
  padding: 12px;
  background: #fff;
  text-align: left;
  cursor: pointer;
}

.plan-card--active {
  border-color: rgba(228, 87, 46, 0.7);
  box-shadow: 0 0 0 1px rgba(228, 87, 46, 0.12);
}

.plan-name {
  font-weight: 900;
  font-size: 14px;
  color: #0b1220;
}

.plan-highlight {
  margin-top: 6px;
  font-size: 12px;
  font-weight: 700;
  color: rgba(15, 23, 42, 0.65);
  line-height: 1.45;
}

.plan-desc {
  margin: 8px 0 0 18px;
  padding: 0;
  color: rgba(15, 23, 42, 0.72);
  font-size: 12px;
  font-weight: 700;
  line-height: 1.45;
}

.plan-prices {
  margin-top: 10px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.plan-price {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  cursor: pointer;
  min-width: 140px;
}

.plan-price:hover {
  box-shadow: 0 6px 18px rgba(11, 17, 32, 0.04);
}

.plan-price__left {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.plan-price__cycle {
  font-size: 12px;
  font-weight: 700;
  color: #0b1220;
  text-transform: capitalize;
}

.plan-price__amount {
  font-size: 14px;
  font-weight: 900;
  color: #e4572e;
}

.plan-price__right {
  display: flex;
  align-items: center;
}

.plan-price--active {
  background: rgba(228, 87, 46, 0.12);
  border-color: rgba(228, 87, 46, 0.6);
  box-shadow: 0 6px 18px rgba(228, 87, 46, 0.08);
}

.selected-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.selected-pill {
  padding: 8px 10px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.06);
  color: #0b1220;
  font-weight: 900;
  font-size: 12px;
}

.selected-pill--primary {
  background: rgba(228, 87, 46, 0.12);
  color: #e4572e;
}

.doc-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.doc-row {
  display: grid;
  grid-template-columns: 28px 1fr;
  gap: 10px;
  align-items: start;
  padding: 10px;
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(15, 23, 42, 0.02);
}

.doc-icon {
  width: 28px;
  height: 28px;
  border-radius: 10px;
  background: rgba(100, 116, 139, 0.1);
  display: grid;
  place-items: center;
}

.doc-copy {
  min-width: 0;
}

.doc-name {
  font-weight: 900;
  font-size: 13px;
  color: #0b1220;
}

.doc-meta {
  margin-top: 4px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 12px;
  font-weight: 800;
  color: rgba(15, 23, 42, 0.6);
}

.doc-path {
  margin-top: 4px;
  font-size: 11px;
  color: rgba(15, 23, 42, 0.55);
  word-break: break-all;
}

.actions {
  margin-top: 16px;
}

.success-row {
}

.success-card {
  width: min(100%, 420px);
  padding: 28px 20px;
  border-radius: 18px;
  text-align: center;
}

.success-icon-wrap {
  display: grid;
  place-items: center;
  width: 72px;
  height: 72px;
  margin: 0 auto 16px;
  color: #16a34a;
  background: #dcfce7;
  border-radius: 50%;
}

.success-kicker {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 26px;
  padding: 4px 10px;
  color: #166534;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.2;
}

.success-title {
  margin: 14px 0 8px;
  color: #0f172a;
  font-size: 22px;
  font-weight: 850;
  line-height: 1.2;
}

.success-subtitle {
  margin: 0 auto 22px;
  max-width: 340px;
  color: #64748b;
  font-size: 14px;
  line-height: 1.45;
}

.success-action {
  min-height: 46px;
  font-weight: 800;
}

.empty-card {
  padding: 14px;
  border-radius: 14px;
  color: #64748b;
  font-weight: 600;
}

.section-title {
  text-transform: capitalize;
}

.section-subtitle {
  margin: 0 0 14px;
  color: #73829a;
  font-size: 14px;
}

.review-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.review-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.review-section-title {
  display: inline-block;
  margin-top: 20px;
  margin-bottom: 6px;
  margin-left: 20px;
  color: #1f2430;
  font-size: 14px;
  font-weight: 700;
}

.review-card {
  border-radius: 14px;
  background: var(--color-white);
  padding: 2px 14px;
}

.review-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
}

.review-row + .review-row {
  border-top: 1px solid #edf0f5;
}

.review-row span {
  color: var(--color-subtitle-light);
  font-size: 14px;
}

.review-row strong {
  color: #1f2430;
  font-size: 14px;
  text-align: right;
  padding-left: 10px;
  font-weight: 600;
}

.review-row--title-bottom {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.review-row--title-bottom strong {
  text-align: left;
  padding-left: 0;
}

.review-file {
  gap: 10px;
  width: 100%;
}

.review-file-thumb {
  width: 56px;
  height: 56px;
  border-radius: 10px;
  object-fit: cover;
}

.review-file-name {
  word-break: break-word;
  font-weight: 400 !important;
}

.confirm-card {
  margin-top: 14px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid var(--border-color);
  padding: 10px;
}

.confirm-card :deep(.v-label) {
  font-size: 14px;
}

.terms-link {
  color: var(--color-theme);
  font-weight: 700;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-decoration: underline;
}

.field-error {
  font-size: 12px;
  color: #b42318;
  margin-top: -4px;
  margin-left: 8px;
}
</style>
